'use client'

import { motion } from 'motion/react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useLocale } from 'next-intl'
import { ReactNode, useCallback, useState } from 'react'
import { LoadingSpinner } from '@/components/loading-spinner'
import { Locale } from '@/setup/locales'
import { getLinkHref, ILinkParams } from '@/setup/routing'
import { cn } from '@/utils/styles/cn'
import { COLORS } from '@/utils/styles/colors'

export interface INavigationMobileItemProps {
    linkParams: ILinkParams | { externalUrl: string; target?: '_blank' | '_self' }
    label: ReactNode
    isActive: boolean
    gtmId?: string
}

export const NavigationMobileItem = ({
    linkParams,
    label,
    isActive,
    gtmId,
}: INavigationMobileItemProps) => {
    const locale = useLocale()
    const [isLoading, setIsLoading] = useState(false)

    const _router = useRouter()

    const onClick = useCallback(() => {
        if (isActive) return

        setIsLoading(true)
    }, [isActive])

    const innerNode = (
        <motion.div
            initial={{
                backgroundColor: isActive ? 'rgba(0, 0, 0, 0.07)' : 'rgba(0, 0, 0, 0)',
            }}
            animate={{
                backgroundColor: isActive ? 'rgba(0, 0, 0, 0.07)' : 'rgba(0, 0, 0, 0)',
            }}
            whileTap={{
                backgroundColor: isActive ? 'rgba(0, 0, 0, 0.07)' : 'rgba(0, 0, 0, 0.02)',
            }}
            transition={{
                backgroundColor: { duration: 0, ease: 'easeOut' },
            }}
            onClick={onClick}
            className={cn(
                `flex items-center px-8 py-[8px] font-semibold relative h-[40px] min-h-[64px] rounded-[16px]
                select-none w-full touch-none`,
                isLoading ? 'justify-between' : 'justify-start'
            )}
        >
            {label}
            {isLoading && <LoadingSpinner size={24} color={COLORS.black} />}
        </motion.div>
    )

    if ('externalUrl' in linkParams) {
        return (
            <a className={gtmId} href={linkParams.externalUrl} target={linkParams.target}>
                {innerNode}
            </a>
        )
    }

    return (
        <Link href={getLinkHref(linkParams, locale as Locale)} className={gtmId}>
            {innerNode}
        </Link>
    )
}
